import React, { forwardRef, HTMLAttributes, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Breakpoint } from 'antd/es/_util/responsiveObserver';
import { useGlobalTokenValue } from '../useGlobalTokenValue';
import { useScreenSize } from '../useScreenSize';

export type StackSize = Breakpoint | 'responsive';

type Align = 'start' | 'center' | 'end';
type Justify = 'space-between' | Align;

export interface StackProps
  extends PropsWithChildren,
    HTMLAttributes<HTMLDivElement> {
  size?: StackSize;
  direction?: 'vertical' | 'horizontal';
  align?: Align;
  justify?: Justify;
}

export const Stack = forwardRef<HTMLDivElement | null, StackProps>(
  (props, ref) => {
    const { direction = 'vertical', align, justify, ...rest } = props;
    const screenSize = useScreenSize();
    const size =
      props.size && props.size !== 'responsive'
        ? props.size
        : screenSize || 'ms';
    const tokenKey = `size${
      size.toUpperCase() as Uppercase<Breakpoint>
    }` as const;
    const gapSize = useGlobalTokenValue(tokenKey);

    return (
      <StackDiv
        ref={ref}
        {...rest}
        $gap={gapSize}
        $direction={direction === 'vertical' ? 'column' : 'row'}
        $align={align}
        $justify={justify}
      />
    );
  }
);

const StackDiv = styled.div<{
  $gap: number;
  $direction: 'column' | 'row';
  $align?: Align;
  $justify?: Justify;
}>`
  display: flex;
  flex-direction: ${(props) => props.$direction};
  gap: ${(props) => props.$gap}px;
  ${({ $align }) =>
    $align
      ? `align-items: ${$align === 'center' ? $align : `flex-${$align}`}`
      : ''};
  ${({ $justify }) =>
    $justify
      ? `justify-content: ${
          $justify === 'center' || $justify === 'space-between'
            ? $justify
            : `flex-${$justify}`
        }`
      : ''};

  // TODO: this should be part of reset.css
  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6,
  p,
  address,
  ol,
  ul,
  dl,
  dd,
  blockquote,
  figure,
  pre,
  legend,
  > .ant-divider {
    margin: 0;
  }
`;
